import { useEffect, useRef, useState } from 'react';

export type AutosaveStatus = 'idle' | 'saved';

interface UseValuationAutosaveProps<T> {
	url?: string | null;
	data: T;
	enabled?: boolean;
	debounceMs?: number;
}

const getXsrfToken = () => {
	const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
	return match ? decodeURIComponent(match[1]) : '';
};

const useValuationAutosave = <T extends object>({
	url,
	data,
	enabled = true,
	debounceMs = 2500,
}: UseValuationAutosaveProps<T>) => {
	const [status, setStatus] = useState<AutosaveStatus>('idle');
	const hasActiveSessionRef = useRef(false);

	useEffect(() => {
		if (!enabled || !url) {
			hasActiveSessionRef.current = false;
			return;
		}

		if (!hasActiveSessionRef.current) {
			hasActiveSessionRef.current = true;
			return;
		}

		setStatus('idle');
		const timeoutId = window.setTimeout(async () => {
			try {
				const xsrfToken = getXsrfToken();
				const response = await fetch(url, {
					method: 'POST',
					credentials: 'same-origin',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
						'X-Requested-With': 'XMLHttpRequest',
						...(xsrfToken ? { 'X-XSRF-TOKEN': xsrfToken } : {}),
					},
					body: JSON.stringify({
						...data,
						autosave: true,
					}),
				});

				if (response.ok) {
					setStatus('saved');
				}
			} catch {
				// Keep silent for MVP autosave behavior.
			}
		}, debounceMs);

		return () => window.clearTimeout(timeoutId);
	}, [data, debounceMs, enabled, url]);

	return status;
};

export default useValuationAutosave;
